// Story 8.5 (ADO-2014), D-20: the "Next cut-off" read over every depot the landing page
// shows. It subscribes to each cart in the shared store (`subscribeToCartCutOff`; Story 8.4,
// ADO-2013: the depot and the account), so a cart whose tile or card also shows a pill costs
// one request, and the refetch and retry rules are the store's (D-11, D-19).
//
//   const next = useNextCutOffs(depots);   // NextCutOffs | null (see nextCutOffs.ts)
//
// The rows hold deadlines, not countdowns: each pill in the line and list ticks on its own
// (`useDepotCutOff`), and the order only changes when an answer does.

import { useEffect, useRef } from 'react';
import { cutOffKey, nextCutOffs, type CutOffDepot, type NextCutOffs } from './nextCutOffs';
import { subscribeToCartCutOff, useCutOffStore } from './useCutOffStore';

export function useNextCutOffs(depots: readonly CutOffDepot[]): NextCutOffs | null {
  // Keyed on the distinct carts, so a new array with the same carts keeps the subscriptions
  // as they are.
  const carts = new Map(
    depots.map((depot) => [
      cutOffKey(depot),
      { depotId: depot.depotId, customerAccountCode: depot.customerAccountCode ?? null },
    ]),
  );
  const cartsKey = [...carts.keys()].join('\n');
  const cartsRef = useRef(carts);
  cartsRef.current = carts;
  useEffect(() => {
    if (cartsKey === '') return;
    const unsubscribes = [...cartsRef.current.values()].map((cart) => subscribeToCartCutOff(cart));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [cartsKey]);
  const views = useCutOffStore((state) => state.views);
  return nextCutOffs(depots, views);
}
